import { ENUM__html_ref_stub } from '@/enums/ENUM__html_ref_stub';
import { EZIndexes } from '@/enums/EZIndexes';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_ref_with_fallback } from '@/hooks/_base/use_ref_with_fallback';
import { cn } from '@/utils/utils_client';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import * as React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { SliderMark } from './slider_mark';
import { SliderMarks } from './slider_marks';
import { SliderMarksTemporary } from './slider_marks_temporary';
import { UI_PRIMITIVE__slider_range_thumb } from './UI_PRIMITIVE__slider_range_thumb';
import { UI_PRIMITIVE__slider_thumb_static } from './UI_PRIMITIVE__slider_thumb_static';

interface SliderProps extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  marks?: SliderMark[];
  max: number;
  min: number;
  //
  value_used: number;
  step?: number;
  //
  onValueChange: (value: number[]) => void;
  onValueCommit: (value: number[]) => void;
  //
  value: number[];
  value_hovered: number;
  defaultValue?: number[];
}

export const UI_PRIMITIVE__slider_range = React.forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(
  (
    { className, marks = [], max, min, value_used, step = 1, value, onValueChange, onValueCommit, value_hovered, defaultValue, disabled = false, ...props },
    ref,
  ) => {
    /*
     * FIELDS
     */
    const ref_slider = use_ref_with_fallback<HTMLDivElement>(ENUM__html_ref_stub.DIV);

    const [show_temporary_marks, set_show_temporary_marks] = useState(false);
    const [is_hovered, set_is_hovered] = useState(false);

    const [value_current, set_value_current] = useState<number[]>(defaultValue || [0, 0]);

    //for dragging range zone
    const [is_range_dragging, set_is_range_dragging] = useState(false);

    const [is_user_edited_slider, set_is_user_edited_slider] = useState(false);

    const pointer_start_X = useRef(0);

    const pointer_start_left = useRef(value[0]);
    const pointer_new_left = useRef(value[0]);

    const move_frame = useRef<number>(NaN);

    const range_width = value[1] - value[0];

    const style = useMemo(
      () => ({
        left: `calc(${calculate_range_position(value[0], min, max)}% + 0.75rem)`,
        width: `calc(${((value[1] - value[0]) / (max - min)) * 100}% - 1.5rem)`,
      }),
      [value, min, max],
    );

    /*
     * EFFECTS
     */

    use_effect(() => {
      document.querySelectorAll('span[role="slider"]').forEach(sliderElement => {
        if (!sliderElement?.parentElement) return;
        sliderElement.parentElement.style.zIndex = '20';
      });
    }, []);

    use_effect(() => set_value_current(value), [value], 'set_value_current');

    use_effect(() => {
      // when we drag right thumb to left thumb for equal value, slider dont call onValueCommit function and we need to call it manually
      if (value_current.length <= 1) return;
      if (value_current[0] !== value_current[1]) return;
      if (value_current[0] == CONSTANTS__shared.NUMBER_EMPTY_VALUE) return;
      if (value_current[1] == CONSTANTS__shared.NUMBER_EMPTY_VALUE) return;
      if (!is_user_edited_slider) return;

      onValueCommit(value_current);
    }, [value_current]);

    /*
     * EVENTS
     */

    /**
     * Handles the start of range dragging when user presses down on the drag control area.
     * This function initializes the drag operation by capturing the pointer and storing
     * the initial position values needed for calculating movement during drag.
     */
    const onPointerDown = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault();

        set_is_range_dragging(true);

        set_is_user_edited_slider(true);

        // Capture the pointer to ensure we receive move/up events even if cursor leaves element
        // This is crucial for smooth dragging experience
        e.currentTarget.setPointerCapture(e.pointerId);

        // Store the initial horizontal position where the drag started
        // This will be used as reference point to calculate how far user has dragged
        pointer_start_X.current = e.clientX;

        // Store the initial left value of the range at drag start
        // This is needed to calculate the new position during drag movement
        pointer_start_left.current = value[0];
      },
      [value],
    );

    /**
     * Handles the continuous movement during range dragging.
     * This function calculates the new range position based on mouse movement,
     * applies step constraints, and updates the slider value in real-time.
     * Uses requestAnimationFrame for smooth performance during rapid mouse movements.
     */
    const onPointerMove = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (!is_range_dragging) return;

        if (!isNaN(move_frame.current)) return;

        // Queue the movement calculation for the next animation frame
        // This ensures smooth performance and prevents excessive calculations
        move_frame.current = requestAnimationFrame(() => {
          const slider = ref_slider.current;

          // Calculate horizontal distance moved since drag started
          const dx = e.clientX - pointer_start_X.current;

          // Get slider dimensions to convert pixel movement to value movement
          const slider_rect = slider.getBoundingClientRect();

          // Calculate the total value range of the slider
          const total_range = max - min;

          // Convert step size to pixels - this determines movement granularity
          // Each step corresponds to a specific pixel distance on the slider
          const pixels_per_step = (slider_rect.width * step) / total_range;

          // Calculate new left position based on mouse movement
          // Round to nearest step to ensure values align with step increments
          let new_left_local = pointer_start_left.current + Math.round(dx / pixels_per_step) * step;

          // Constrain the new position to valid bounds
          // Ensure range doesn't go below min or push the right edge beyond max
          new_left_local = Math.max(min, Math.min(new_left_local, max - range_width));

          // Snap to step boundaries to maintain step alignment
          new_left_local = Math.round((new_left_local - min) / step) * step + min;

          // Final boundary check - ensure right edge doesn't exceed maximum
          if (new_left_local + range_width > max) {
            new_left_local = max - range_width;
          }

          pointer_new_left.current = new_left_local;

          onValueChange([new_left_local, new_left_local + range_width]);

          move_frame.current = NaN;
        });
      },
      [is_range_dragging, max, min, step, range_width, onValueChange],
    );

    /**
     * Handles the end of range dragging when user releases the pointer.
     * This function finalizes the drag operation by releasing pointer capture,
     * resetting drag state, and committing the final value change.
     */
    const onPointerUp = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (!is_range_dragging) return;

        set_is_range_dragging(false);

        // Release pointer capture to return normal pointer behavior
        // This is important for cleanup and preventing stuck capture states
        e.currentTarget.releasePointerCapture(e.pointerId);

        let min_final = pointer_new_left.current === value[0] ? value[0] : pointer_new_left.current;
        let max_final = min_final + range_width;

        onValueCommit([min_final, max_final]);
      },
      [is_range_dragging, value, range_width, onValueCommit],
    );

    function onDoubleClick() {
      if (disabled) return;

      onValueChange([min, max]);
      onValueCommit([min, max]);
    }

    /*
     * METHODS
     */

    function calculate_range_position(value: number, min: number, max: number): number {
      return ((value - min) / (max - min)) * 100;
    }

    /*
     * RENDERS
     */

    function render_thumbs() {
      if (show_temporary_marks) return null;
      if (!isNaN(value_hovered)) return null; //thumb value hovered is visible

      let thumb_props = { disabled, value_used, min, max };

      return (
        <>
          <UI_PRIMITIVE__slider_range_thumb value_range={value_current} {...thumb_props} is_left />
          <UI_PRIMITIVE__slider_range_thumb value_range={value_current} {...thumb_props} />
        </>
      );
    }

    function render_thumb_value_used() {
      if (isNaN(value_used)) return null;
      if (!isNaN(value_hovered)) return null; //thumb value hovered is visible
      if (value_used === value_current[0] || value_used === value_current[1]) return null; //do not conflict with other thumbs
      if (is_hovered) return null; //do not conflict with user actions
      if (show_temporary_marks) return null;

      return <UI_PRIMITIVE__slider_thumb_static value={value_used} {...{ min, max }} />;
    }

    function render_thumb_value_hovered() {
      if (isNaN(value_hovered)) return null;
      if (show_temporary_marks) return null;

      return <UI_PRIMITIVE__slider_thumb_static value={value_hovered} {...{ min, max }} />;
    }

    /*
     * HTML
     */

    return (
      <div
        onMouseEnter={() => set_is_hovered(true)}
        onMouseLeave={() => set_is_hovered(false)}
        {...{ onDoubleClick }}
        className={cn('w-full max-w-xl', className)}
      >
        <div ref={ref_slider} className="relative">
          <SliderPrimitive.Root
            className={cn(
              `
              ui-primitive-slider-root
              relative flex items-center
              w-full 
              touch-none select-none `,
              className,
            )}
            value={value_current}
            {...{ ref, step, min, max, defaultValue, onValueChange, onValueCommit, disabled }}
            {...props}
          >
            <SliderPrimitive.Track
              className={`
                ui-primitive-slider-track 
                relative grow overflow-hidden
                h-2 w-full 
                cursor-pointer    
                bg-component rounded-full 
                border border-1 
                ${show_temporary_marks ? 'border-component-item-border' : 'border-component-item'} 
                ${value_current[0] === min ? '' : 'rounded-l-full'} 
                ${value_current[1] === max ? '' : 'rounded-r-full'}`}
            >
              <SliderPrimitive.Range
                className={`ui-primitive-slider-range absolute h-full 
                  cursor-pointer bg-component-item 
                  duration-300 transition-opacity 
                  ${show_temporary_marks ? 'opacity-00' : 'opacity-100'}`}
              />
            </SliderPrimitive.Track>

            <SliderMarks {...{ marks, max, step }} />

            <SliderMarksTemporary {...{ min, max, step, set_show_temporary_marks, show_temporary_marks }} />

            {render_thumbs()}

            {render_thumb_value_hovered()}

            {render_thumb_value_used()}
          </SliderPrimitive.Root>
          <div
            className={`drag-block absolute top-1/2 left-0 h-2 -mt-1 ${EZIndexes.SLIDER_RANGE_DRAG_CONTROL} cursor-grab 
            ${is_range_dragging ? 'cursor-grabbing' : ''}`}
            {...{ onPointerDown, onPointerMove, onPointerUp, style }}
          />
        </div>
      </div>
    );
  },
);

UI_PRIMITIVE__slider_range.displayName = 'SliderRange';
